/*!
 * LucyBalu Wand-Planer  (assets/lb-wall-planner.js)
 * --------------------------------------------------------------------------
 * Gehört zu sections/lb-wall-planner.liquid. Die Section liefert die Konfiguration
 * als JSON (<script type="application/json" data-lbwp-config>), dieses Skript baut
 * daraus die interaktive Oberfläche.
 *
 * Aufbau
 *   1. SHAPES      Zeichnungen der Produkte (SVG-Pfade in cm). Neues Produkt =
 *                  neuen Eintrag hier + Option im Schema der Section.
 *   2. Hilfen      Geld, Varianten-Zuordnung, Teilen-Link
 *   3. Planner     Zustand, Rendering, Ziehen/Drehen, Warenkorb, Foto, Export
 *
 * Koordinaten: alles in cm. x = linke Kante ab linker Wandkante,
 * top = Oberkante des Elements über dem Boden. Die Breite kommt aus dem
 * Varianten-Metafeld custom.wall_width_cm (sonst aus der Zeichnung), die Höhe
 * ergibt sich aus dem Seitenverhältnis der Zeichnung (Ansicht leicht von oben,
 * sichtbare Höhe ≈ 0,97 × Höhe + 0,26 × Tiefe).
 *
 * Ereignisse für Tracking (auf dem Section-Element, bubbles):
 *   lbwp:preset, lbwp:add, lbwp:remove, lbwp:share, lbwp:export,
 *   lbwp:add-to-cart  (detail enthält jeweils die relevanten Daten)
 *
 * Öffentliche API: window.LBWallPlanner = { shapes, instances, init }
 */
(function () {
  'use strict';

  /* ======================================================================
   * 1. SHAPES – Produktzeichnungen
   *    Jede Funktion bekommt die Palette P und liefert
   *    { w: Breite, h: Höhe (beides in Zeichnungs-Einheiten = cm), paths: [...] }
   *    Ein Pfad: { d, f: Füllung, s: Linie, w: Linienstärke }
   * ==================================================================== */
  var n2 = function (v) { return Math.round(v * 100) / 100; };
  function R(x, y, w, h, r) {
    r = Math.min(r || 0, w / 2, h / 2);
    if (!r) return 'M' + n2(x) + ',' + n2(y) + 'h' + n2(w) + 'v' + n2(h) + 'h' + n2(-w) + 'z';
    return 'M' + n2(x + r) + ',' + n2(y) + 'h' + n2(w - 2 * r) + 'a' + r + ',' + r + ' 0 0 1 ' + r + ',' + r +
      'v' + n2(h - 2 * r) + 'a' + r + ',' + r + ' 0 0 1 ' + (-r) + ',' + r + 'h' + n2(-(w - 2 * r)) +
      'a' + r + ',' + r + ' 0 0 1 ' + (-r) + ',' + (-r) + 'v' + n2(-(h - 2 * r)) + 'a' + r + ',' + r + ' 0 0 1 ' + r + ',' + (-r) + 'z';
  }
  function E(cx, cy, rx, ry) {
    return 'M' + n2(cx - rx) + ',' + n2(cy) + 'a' + rx + ',' + ry + ' 0 1 0 ' + n2(2 * rx) + ',0a' + rx + ',' + ry + ' 0 1 0 ' + n2(-2 * rx) + ',0z';
  }
  function G(pts) { return 'M' + pts.map(function (p) { return n2(p[0]) + ',' + n2(p[1]); }).join('L') + 'z'; }
  function fill(P, d, color, edge) { return { d: d, f: color, s: edge ? P.fe : 'none', w: edge ? 1 : 0 }; }
  function hatch(P, x, y, w, h, step) {
    var d = '';
    for (var yy = y + step; yy < y + h; yy += step) d += 'M' + n2(x) + ',' + n2(yy) + 'h' + n2(w);
    return { d: d, f: 'none', s: P.sl, w: 1 };
  }

  var SHAPES = {
    swing: function (P) {
      return { w: 65, h: 15, paths: [
        fill(P, R(1, 0, 63, 5.4, 2.7), P.fF, true),
        fill(P, R(1.3, 3.5, 2.6, 8.8, 1.3), P.fT, true),
        fill(P, R(61.1, 3.5, 2.6, 8.8, 1.3), P.fT, true),
        fill(P, 'M3.6,3.9L61.4,3.9L62.6,11.2Q32.5,13.6 2.4,11.2Z', P.lT),
        fill(P, 'M2.4,11.2Q32.5,13.6 62.6,11.2L62.6,12.1Q32.5,14.5 2.4,12.1Z', P.lF),
        fill(P, E(2.6, 12.2, 2.6, 2.5), P.fF, true),
        fill(P, E(62.4, 12.2, 2.6, 2.5), P.fF, true)] };
    },
    scratchpad: function (P) {
      return { w: 30, h: 60, paths: [
        fill(P, R(0, 0, 30, 60, 2.5), P.fF, true),
        fill(P, R(1.1, 1.1, 27.8, 57.8, 1.8), P.sF),
        hatch(P, 1.1, 1.1, 27.8, 57.8, 1.6)] };
    },
    steps: function (P) {
      return { w: 18, h: 7.5, paths: [
        fill(P, R(3, 0, 12, 1.6, 0.4), P.fF, true),
        fill(P, R(0, 0.8, 18, 5.6, 1), P.fT, true),
        fill(P, R(0.9, 1.2, 16.2, 4.4, 0.7), P.lT),
        fill(P, R(0, 6, 18, 1.4, 0.6), P.fF, true)] };
    },
    stepsxl: function (P) {
      /* STEPS XL: 24 x 20 cm, gleiche Optik wie STEPS, etwas tiefer */
      return { w: 24, h: 8, paths: [
        fill(P, R(4, 0, 16, 1.7, 0.4), P.fF, true),
        fill(P, R(0, 0.8, 24, 6, 1), P.fT, true),
        fill(P, R(0.9, 1.2, 22.2, 4.8, 0.7), P.lT),
        fill(P, R(0, 6.5, 24, 1.5, 0.6), P.fF, true)] };
    },
    catwalkxl: function (P) {
      /* CATWALK Extra lang: 100 cm */
      return { w: 9, h: 102, paths: [
        fill(P, R(0.25, 0.9, 8.5, 100.2, 1.8), P.sF),
        hatch(P, 0.25, 1.4, 8.5, 99.2, 1.5),
        fill(P, E(4.5, 1.3, 4.25, 1), P.sT),
        fill(P, R(2.2, 0, 4.6, 1.3, 0.5), P.fT, true),
        fill(P, R(2.2, 100.7, 4.6, 1.3, 0.5), P.fT, true)] };
    },
    catwalk: function (P) {
      return { w: 9, h: 72, paths: [
        fill(P, R(0.25, 0.9, 8.5, 70.2, 1.8), P.sF),
        hatch(P, 0.25, 1.4, 8.5, 69.2, 1.5),
        fill(P, E(4.5, 1.3, 4.25, 1), P.sT),
        fill(P, R(2.2, 0, 4.6, 1.3, 0.5), P.fT, true),
        fill(P, R(2.2, 70.7, 4.6, 1.3, 0.5), P.fT, true)] };
    },
    wave: function (P) {
      return { w: 92, h: 32.5, paths: [
        fill(P, G([[40, 30.8], [53, 7.5], [53, 9], [40, 32.3]]), P.fF, true),
        fill(P, G([[40, 23.3], [53, 0], [53, 7.5], [40, 30.8]]), P.fT, true),
        fill(P, R(53, 0, 39, 7.5, 0.6), P.fT, true),
        fill(P, R(55, 0.8, 35, 5.9, 0.5), P.lT),
        fill(P, R(53, 7.5, 39, 1.5, 0.4), P.fF, true),
        fill(P, R(0, 23.3, 40, 7.5, 0.6), P.fT, true),
        fill(P, R(2, 24.1, 36, 5.9, 0.5), P.lT),
        fill(P, R(0, 30.8, 40, 1.5, 0.4), P.fF, true)] };
    },
    jumper: function (P) {
      return { w: 9.2, h: 14, paths: [
        fill(P, E(4.6, 2.3, 4.6, 2.3), P.fT, true),
        fill(P, R(0, 2.3, 9.2, 7, 0), P.fT),
        fill(P, E(4.6, 9.3, 4.6, 4.5), P.fF, true),
        { d: E(4.6, 9.3, 3.1, 3) + E(4.6, 9.3, 1.6, 1.5), f: 'none', s: P.sl, w: 1 }] };
    },
    straight: function (P) {
      return { w: 65, h: 25.5, paths: [
        fill(P, R(0, 0, 65, 14.6, 0.6), P.fF, true),
        fill(P, E(10, 4, 0.6, 0.6) + E(55, 4, 0.6, 0.6), P.fe),
        fill(P, R(0, 14.6, 65, 8.8, 0), P.fT, true),
        fill(P, R(1, 14.9, 63, 8.1, 0.5), P.lT),
        fill(P, R(0, 23.2, 65, 2.3, 0.6), P.fF, true)] };
    },
    corner: function (P) {
      return { w: 36.5, h: 16, paths: [
        fill(P, R(0, 0, 36.5, 4.9, 0.5), P.fF, true),
        fill(P, 'M0,14.5Q20,14.5 36.5,4.9L36.5,6.3Q20,15.9 0,15.9Z', P.fF, true),
        fill(P, 'M0,4.9L36.5,4.9Q20,14.5 0,14.5Z', P.fT, true),
        fill(P, 'M1.4,5.5L33.5,5.5Q19,13.5 1.4,13.7Z', P.lT)] };
    },
    plateau: function (P) {
      return { w: 50, h: 92, over: 14, paths: [
        fill(P, R(0.5, 90.6, 49, 1.4, 0.4), P.fF, true),
        fill(P, R(0.5, 78, 49, 12.7, 1.2), P.fT, true),
        fill(P, R(20.5, 15.5, 9, 69, 0.8), P.sF),
        hatch(P, 20.5, 16, 9, 68, 1.6),
        fill(P, R(1, 0, 48, 6.5, 0.8), P.fF, true),
        fill(P, R(0, 5, 50, 9.6, 0.8), P.fT, true),
        fill(P, R(1.2, 5.6, 47.6, 8.4, 0.6), P.lT),
        fill(P, R(0, 14.6, 50, 2.2, 0.6), P.fF, true)] };
    },
    scratchpole: function (P) {
      return { w: 45, h: 80.5, over: 12.8, paths: [
        fill(P, E(22.5, 74.5, 22.5, 5.9), P.fF, true),
        fill(P, E(22.5, 73.6, 22.5, 5.9), P.fT, true),
        fill(P, R(18, 1.3, 9, 72.3, 0), P.sF),
        hatch(P, 18, 1.5, 9, 72, 1.6),
        fill(P, E(22.5, 1.3, 4.6, 1.25), P.fT, true)] };
    }
  };

  /* Farben der Zeichnungen: Gestell (w = Weiß, a = Anthrazit) × Filz/Sisal (s = Sand, a = Anthrazit) */
  function palette(frame, felt) {
    var f = frame === 'w' ? { fF: '#fbfbfa', fT: '#ecebe7', fe: '#b3aca1' } : { fF: '#3d3f42', fT: '#4f5256', fe: '#2e3033' };
    var l = felt === 'a'
      ? { lT: '#5f6165', lF: '#46484b', sF: '#5b5c5f', sT: '#6c6d70', sl: 'rgba(0,0,0,0.32)' }
      : { lT: '#d8c6a5', lF: '#c2ab84', sF: '#c9ad83', sT: '#d9c3a0', sl: 'rgba(90,70,40,0.28)' };
    var o = {}, k;
    for (k in f) o[k] = f[k];
    for (k in l) o[k] = l[k];
    return o;
  }
  /* Nur zwei Farbwelten: Weiß = Gestell Weiß + Filz/Sisal Sand, Anthrazit = alles Anthrazit */
  var FELT_FOR = { w: 's', a: 'a' };
  var SWATCH = { frame: { w: '#fbfbfa', a: '#3d3f42' }, felt: { s: '#d6c3a1', a: '#55575b' } };

  /* Boden vor der Wand (cm, Ansicht leicht von oben). Bodenelemente stehen mit der
   * hinteren Kante auf der Linie Wand/Boden, der vordere Teil ragt auf den Boden. */
  var FLOOR_CM = 30;
  var OAK = ['#c9a374', '#c19a6b', '#d0ab7d', '#bb9363', '#c6a070', '#cfa878'];
  /* Eichenparkett als SVG (Breite in cm, Höhe = FLOOR_CM) */
  function parquet(wcm, px) {
    var rows = 4, rh = FLOOR_CM / rows, out = '', seed = 7;
    var rnd = function () { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (var r = 0; r < rows; r++) {
      var x = -rnd() * 120, y = r * rh;
      while (x < wcm) {
        var len = 110 + rnd() * 80, c = OAK[Math.floor(rnd() * OAK.length)];
        out += '<rect x="' + n2(x) + '" y="' + n2(y) + '" width="' + n2(len) + '" height="' + n2(rh) + '" fill="' + c + '"/>' +
          '<rect x="' + n2(x) + '" y="' + n2(y + rh * 0.35) + '" width="' + n2(len) + '" height="' + n2(rh * 0.08) + '" fill="rgba(120,85,45,.10)"/>' +
          '<rect x="' + n2(x + len - 0.35) + '" y="' + n2(y) + '" width="0.35" height="' + n2(rh) + '" fill="rgba(80,55,30,.35)"/>';
        x += len;
      }
      out += '<rect x="0" y="' + n2(y + rh - 0.25) + '" width="' + wcm + '" height="0.25" fill="rgba(80,55,30,.35)"/>';
    }
    out += '<rect x="0" y="0" width="' + wcm + '" height="1.2" fill="rgba(60,45,30,.18)"/>';
    return '<svg ' + (px ? 'xmlns="http://www.w3.org/2000/svg" width="' + px[0] + '" height="' + px[1] + '" ' : 'aria-hidden="true" ') +
      'viewBox="0 0 ' + n2(wcm) + ' ' + FLOOR_CM + '" preserveAspectRatio="none">' + out + '</svg>';
  }

  /* Planungshilfe: welche Gruppen/Elemente als Liegeplatz bzw. Kratzplatz gelten */
  var RULES = {
    restGroups: ['liegen'], restKeys: ['plateau'],
    scratchGroups: ['kratzen', 'frei'],
    entryMaxCm: 100 /* Unterkante des tiefsten Wandelements für einen leichten Einstieg */
  };

  /* ======================================================================
   * 2. Hilfen
   * ==================================================================== */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(str, vars) {
    return String(str || '').replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; });
  }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function formatMoney(cents, format) {
    format = format || '{{amount_with_comma_separator}} €';
    var m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var type = m ? m[1] : 'amount';
    function delim(num, prec, th, dec) {
      var s = (num / 100).toFixed(prec).split('.');
      return s[0].replace(/\B(?=(\d{3})+(?!\d))/g, th) + (s[1] ? dec + s[1] : '');
    }
    var v;
    switch (type) {
      case 'amount_no_decimals': v = delim(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = delim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = delim(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': v = delim(cents, 2, ' ', ','); break;
      default: v = delim(cents, 2, ',', '.');
    }
    return m ? format.replace(m[0], v) : v;
  }

  /* Variantenoptionen → Gestell/Filz. Funktioniert mit übersetzten Optionswerten
   * (DE/EN/FR/IT/NL). Reihenfolge: erster Farbteil = Gestell, zweiter = Filz/Sisal.
   * Beispiele: "Gestell Weiß - Sisal Sand", "Anthrazit / FELT PADS Sand", "Weiß - Sand". */
  var RX = {
    white: /wei(ß|ss)|white|blanc|bianc|\bwit\b/i,
    anth: /anthra|antraci/i,
    sand: /sand|sable|sabbia|zand|creme|crème|crema/i
  };
  function classify(options) {
    var parts = [];
    (options || []).forEach(function (o) {
      String(o).split(/\s+[-–]\s+|\s*:\s*/).forEach(function (p) { parts.push(p); });
    });
    var frame = null, felt = null;
    parts.forEach(function (p) {
      var w = RX.white.test(p), a = RX.anth.test(p), s = RX.sand.test(p);
      if (!w && !a && !s) return;
      if (frame === null && (w || a) && !s) { frame = w ? 'w' : 'a'; return; }
      if (felt === null) felt = s ? 's' : (a ? 'a' : 'x');
    });
    return { frame: frame, felt: felt };
  }

  /* ======================================================================
   * 3. Planner
   * ==================================================================== */
  function Planner(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    /* Der Liquid-Filter t liefert HTML-escapte Texte (&amp;) – hier zurückwandeln, esc() übernimmt das Escapen */
    var dec = document.createElement('textarea');
    var unesc = function (v) { dec.innerHTML = v == null ? '' : String(v); return dec.value; };
    this.t = {};
    for (var tk in (cfg.t || {})) this.t[tk] = unesc(cfg.t[tk]);
    (cfg.groups || []).forEach(function (g) { g.title = unesc(g.title); g.hint = unesc(g.hint); });
    this.els = {};
    this.order = [];
    var self = this;
    (cfg.elements || []).forEach(function (el) {
      if (!SHAPES[el.shape]) return;
      el.variants = (el.variants || []).map(function (v) {
        var c = classify(v.options);
        v.frame = c.frame; v.felt = c.felt;
        return v;
      });
      el.available = el.variants.some(function (v) { return v.available; });
      el.setSize = Math.max(1, el.set_size || 1);
      el.key = el.shape;
      if (self.els[el.key]) return; /* ein Element je Zeichnung */
      self.els[el.key] = el;
      self.order.push(el.key);
    });
    this.seq = 1;
    this.state = {
      frame: cfg.defaults.frame === 'a' ? 'a' : 'w',
      felt: cfg.defaults.frame === 'a' ? 'a' : 's',
      wallW: cfg.wall.w, wallH: cfg.wall.h,
      items: [], sel: null, pick: {}, photo: null, photoSize: null, lines: { top: 0.06, floor: 0.82 }, showPhoto: false, saved: false, busy: false, msg: ''
    };
    var shared = this.readShare();
    if (shared) {
      this.state.frame = shared.frame; this.state.felt = FELT_FOR[shared.frame];
      this.state.wallW = shared.wallW; this.state.wallH = shared.wallH;
      this.state.items = shared.items;
    } else if (cfg.presets && cfg.presets.length) {
      this.state.items = this.presetItems(cfg.presets[0]);
    }
    this.build();
    this.render();
  }

  var p = Planner.prototype;

  /* ---------- Daten ---------- */
  /* Welche Variante gilt für ein Element bei der gewählten Farbe?
   *   status 'ok'      – genau diese Farbe, bestellbar (oder bewusst gewählte Alternative)
   *   status 'soldout' – die Farbe gibt es, sie ist aber ausverkauft. Wird in der gewählten
   *                      Farbe gezeichnet, als ausverkauft markiert und nicht in den Warenkorb gelegt.
   *   status 'combo'   – die Farbkombination gibt es bei diesem Produkt nicht (z. B. WAVE nur
   *                      Weiß/Sand oder Anthrazit/Anthrazit). Gezeichnet wird die nächstliegende,
   *                      bestellbare Variante, mit Hinweis.
   * Welche Variante zu Weiß bzw. Anthrazit gehört: SKU aus dem Element (sku.w / sku.a),
 * sonst automatisch Weiß → Gestell Weiß + Sand, Anthrazit → Anthrazit + Anthrazit. */
  p.resolve = function (el) {
    var s = this.state, sku = el.sku && el.sku[s.frame], exact = null, best = null, bestScore = -1;
    if (sku) exact = el.variants.filter(function (v) { return v.sku === sku; })[0] || null;
    if (!exact) exact = el.variants.filter(function (v) {
      return (v.frame === null || v.frame === s.frame) && (v.felt === null || v.felt === 'x' || v.felt === FELT_FOR[s.frame]);
    })[0] || null;
    if (exact && exact.available) return { v: exact, status: 'ok', alt: null };
    if (exact) return { v: exact, status: 'soldout', alt: null };
    el.variants.forEach(function (v) {
      if (!v.available) return;
      var sc = (v.frame === s.frame ? 2 : 0) + (v.felt === null || v.felt === FELT_FOR[s.frame] ? 1 : 0);
      if (sc > bestScore) { best = v; bestScore = sc; }
    });
    return { v: best || el.variants[0] || null, status: 'combo', alt: null };
  };
  p.variantFor = function (el) { return this.resolve(el).v; };
  p.colorLabel = function (frame, felt, withFelt) {
    var t = this.t, F = { w: t.white, a: t.anthracite }, L = { s: t.sand, a: t.anthracite };
    return (F[frame] || '') + (withFelt && L[felt] ? ' / ' + L[felt] : '');
  };
  p.chosenLabel = function (el) {
    var hasFelt = el.variants.some(function (v) { return v.felt !== null; });
    return this.colorLabel(this.state.frame, this.state.felt, hasFelt);
  };
  p.variantLabel = function (v) {
    return v ? this.colorLabel(v.frame, v.felt, v.felt !== null && v.felt !== 'x') || v.title : '';
  };
  p.geom = function (key) {
    var el = this.els[key], rv = this.resolve(el), v = rv.v;
    var pal = palette(v && v.frame ? v.frame : this.state.frame, v && v.felt && v.felt !== 'x' ? v.felt : this.state.felt);
    var sh = SHAPES[el.shape](pal);
    var w = (v && v.w > 0) ? v.w : sh.w;
    var k = w / sh.w;
    return { el: el, v: v, status: rv.status, alt: rv.alt, shape: sh, w: w, h: sh.h * k, over: (sh.over || 0) * k };
  };
  p.presetItems = function (pr) {
    var self = this, out = [];
    (pr.items || []).forEach(function (row) {
      var el = self.els[row[0]];
      if (!el || !el.available) return;
      var g = self.geom(row[0]);
      out.push(self.place({ id: 'i' + (self.seq++), key: row[0], x: row[1], top: el.floor ? g.h - g.over : row[2], rot: row[3] || 0, flip: !!(el.mirrorable && row[4]) }));
    });
    return out;
  };
  p.place = function (it) {
    var g = this.geom(it.key), s = this.state;
    it.x = clamp(it.x, 0, Math.max(0, s.wallW - g.w));
    it.top = g.el.floor ? g.h - g.over : clamp(it.top, Math.min(g.h, s.wallH), s.wallH);
    return it;
  };
  p.totals = function () {
    var self = this, s = this.state, lines = {}, order = [];
    s.items.forEach(function (it) {
      if (!lines[it.key]) { lines[it.key] = { key: it.key, n: 0, ids: [] }; order.push(it.key); }
      lines[it.key].n++; lines[it.key].ids.push(it.id);
    });
    var res = { lines: [], count: 0, eligible: 0, sub: 0, eligibleSub: 0, soldout: 0 };
    order.forEach(function (key) {
      var L = lines[key], g = self.geom(key), el = g.el, v = g.v;
      var qty = Math.ceil(L.n / el.setSize);
      var price = v ? v.price : 0;
      res.lines.push({ key: key, el: el, v: v, status: g.status, alt: g.alt, placed: L.n, qty: qty, free: qty * el.setSize - L.n, price: price, ids: L.ids });
      if (g.status === 'soldout') { res.soldout += qty; return; }
      res.count += qty; res.sub += price * qty;
      if (el.eligible) { res.eligible += qty; res.eligibleSub += price * qty; }
    });
    /* Rabattstaffel: höchste erreichte Stufe gilt (wie bei nicht kombinierbaren Shopify-Rabatten) */
    var tiers = this.tiers();
    res.tier = null; res.next = tiers[0] || null;
    tiers.forEach(function (tr, i) { if (res.eligible >= tr.min) { res.tier = tr; res.next = tiers[i + 1] || null; } });
    res.unlocked = !!res.tier;
    res.pct = res.tier ? res.tier.pct : 0;
    res.disc = res.unlocked ? Math.round(res.eligibleSub * res.pct / 100) : 0;
    res.total = res.sub - res.disc;
    return res;
  };
  p.tiers = function () {
    var d = this.cfg.discount;
    if (!d || !d.tiers || (d.mode === 'code' && !d.code)) return [];
    return d.tiers.filter(function (tr) { return tr.min > 0 && tr.pct > 0; }).sort(function (a, b) { return a.min - b.min; });
  };
  p.money = function (c) { return formatMoney(c, this.cfg.moneyFormat); };
  p.emit = function (name, detail) {
    try { this.root.dispatchEvent(new CustomEvent('lbwp:' + name, { bubbles: true, detail: detail || {} })); } catch (e) {}
  };
  p.set = function (patch) {
    for (var k in patch) this.state[k] = patch[k];
    this.render();
  };

  /* ---------- Teilen-Link: ?wand=a-s-320-250_swing-118-182_catwalk-39-110-15 ---------- */
  p.shareString = function () {
    var s = this.state;
    return [s.frame, s.felt, Math.round(s.wallW), Math.round(s.wallH)].join('-') + '_' +
      s.items.map(function (it) {
        return [it.key, Math.round(it.x), Math.round(it.top)].concat(it.rot || it.flip ? [it.rot || 0] : []).concat(it.flip ? ['m'] : []).join('-');
      }).join('_');
  };
  p.readShare = function () {
    try {
      var q = new URLSearchParams(window.location.search).get('wand');
      if (!q) return null;
      var parts = q.split('_'), head = parts.shift().split('-');
      var self = this;
      var res = {
        frame: head[0] === 'w' ? 'w' : 'a', felt: head[0] === 'w' ? 's' : 'a',
        wallW: clamp(+head[2] || this.cfg.wall.w, 50, 1000), wallH: clamp(+head[3] || this.cfg.wall.h, 50, 600), items: []
      };
      this.state.frame = res.frame; this.state.felt = FELT_FOR[res.frame]; this.state.wallW = res.wallW; this.state.wallH = res.wallH;
      parts.slice(0, 40).forEach(function (r) {
        var a = r.split('-'), el = self.els[a[0]];
        if (!el || !el.available) return;
        res.items.push(self.place({ id: 'i' + (self.seq++), key: a[0], x: +a[1] || 0, top: +a[2] || 0, rot: el.rotatable ? (+a[3] || 0) : 0, flip: !!(el.mirrorable && a[4] === 'm') }));
      });
      return res;
    } catch (e) { return null; }
  };

  /* ---------- Gerüst (einmalig) ---------- */
  p.build = function () {
    var t = this.t, cfg = this.cfg, self = this;
    var pill = function (grp, val, label, color) {
      return '<button type="button" class="lbwp-pill" data-act="' + grp + '" data-val="' + val + '"><span class="lbwp-dot" style="background:' + color + '"></span>' + esc(label) + '</button>';
    };
    var dual = function (f) { return 'linear-gradient(135deg,' + SWATCH.frame[f] + ' 0 50%,' + SWATCH.felt[FELT_FOR[f]] + ' 50% 100%)'; };
    var icon = {
      cam: '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h3l2-3h8l2 3h3v11H3z"/><circle cx="12" cy="13" r="3.5"/></svg>',
      link: '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>'
    };
    var presets = (cfg.presets || []).map(function (pr, i) {
      return '<button type="button" class="lbwp-btn lbwp-btn--outline" data-act="preset" data-val="' + i + '">' + esc(self.presetLabel(pr)) + '</button>';
    }).join('');

    var app = this.root.querySelector('[data-lbwp-app]');
    app.innerHTML =
      (presets ? '<div class="lbwp-presets"><span class="lbwp-presets__label">' + esc(cfg.presetsHeading) + '</span>' + presets +
        '<button type="button" class="lbwp-link" data-act="clear">' + esc(t.clear) + '</button></div>' : '') +
      '<div class="lbwp-layout">' +
        '<div class="lbwp-main">' +
          '<div class="lbwp-card lbwp-stage">' +
            '<div class="lbwp-toolbar">' +
              '<div class="lbwp-colors">' +
                '<div class="lbwp-choice" role="group" aria-label="' + esc(t.frame) + '"><span class="lbwp-choice__label">' + esc(t.frame) + '</span>' +
                  pill('frame', 'w', t.white, dual('w')) + pill('frame', 'a', t.anthracite, dual('a')) + '</div>' +
              '</div>' +
              '<div class="lbwp-toolbar__right"><span class="lbwp-muted" data-r="wallLabel"></span>' +
                (cfg.photo ? '<button type="button" class="lbwp-textbtn" data-act="togglePhoto" aria-expanded="false">' + icon.cam + esc(t.own_photo) + '</button>' : '') +
              '</div>' +
            '</div>' +
            (cfg.photo ?
            '<div class="lbwp-photo" data-r="photo" hidden>' +
              '<div class="lbwp-photo__tip"><svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h3l2-3h8l2 3h3v11H3z"/><circle cx="12" cy="13" r="3.5"/></svg><div><strong>' + esc(t.photo_tip_title) + '</strong><p>' + esc(t.photo_tip) + '</p></div></div>' +
              '<div class="lbwp-photo__row">' +
                '<label class="lbwp-file">' + icon.cam + '<span data-r="photoBtn">' + esc(t.photo_pick) + '</span><input type="file" accept="image/*" data-act="photoFile"></label>' +
                '<label class="lbwp-field">' + esc(t.wall_width) + '<input type="number" inputmode="numeric" min="50" max="1000" data-act="wallW"></label>' +
                '<label class="lbwp-field">' + esc(t.wall_height) + '<input type="number" inputmode="numeric" min="50" max="600" data-act="wallH"></label>' +
                '<div class="lbwp-photo__actions" data-r="photoActions" hidden>' +
                  '<button type="button" class="lbwp-textbtn" data-act="export">' + esc(t.save_image) + '</button>' +
                  '<button type="button" class="lbwp-link" data-act="removePhoto">' + esc(t.remove_photo) + '</button>' +
                '</div>' +
              '</div>' +
              '<p class="lbwp-muted" data-r="photoHint"></p>' +
              '<div class="lbwp-mark" data-r="mark" hidden><p class="lbwp-mark__title">' + esc(t.mark_title) + '</p><p class="lbwp-muted">' + esc(t.mark_hint) + '</p>' +
                '<div class="lbwp-mark__box" data-r="markBox"><img alt="" data-r="markImg">' +
                  '<div class="lbwp-mark__line is-top" data-line="top" tabindex="0" role="slider" aria-label="' + esc(t.line_top) + '"><span data-r="lineTopLabel"></span></div>' +
                  '<div class="lbwp-mark__line is-floor" data-line="floor" tabindex="0" role="slider" aria-label="' + esc(t.line_floor) + '"><span>' + esc(t.line_floor) + '</span></div>' +
                '</div></div>' +
            '</div>' : '') +
            '<div class="lbwp-wall" data-r="wall"></div>' +
            '<div data-r="selbar"></div>' +
          '</div>' +
          '<div class="lbwp-card"><h3 class="lbwp-h3">' + esc(t.add_title) + '</h3><div class="lbwp-groups" data-r="groups"></div></div>' +
        '</div>' +
        '<aside class="lbwp-aside">' +
          '<div class="lbwp-card lbwp-summary">' +
            '<div class="lbwp-summary__head"><h3 class="lbwp-h3">' + esc(t.your_wall) + '</h3><span class="lbwp-muted" data-r="count"></span></div>' +
            '<div data-r="progress"></div>' +
            '<div class="lbwp-lines" data-r="lines"></div>' +
            '<div class="lbwp-sums" data-r="sums"></div>' +
            '<div class="lbwp-ctas">' +
              '<button type="button" class="lbwp-btn lbwp-btn--primary" data-act="cart">' + esc(cfg.ctaLabel) + '</button>' +
              '<button type="button" class="lbwp-textbtn lbwp-textbtn--center" data-act="share">' + icon.link + '<span data-r="shareLabel">' + esc(t.share) + '</span></button>' +
            '</div>' +
            '<p class="lbwp-msg" data-r="msg" role="status" aria-live="polite"></p>' +
            (cfg.discountNote ? '<p class="lbwp-small">' + esc(cfg.discountNote) + '</p>' : '') +
          '</div>' +
          '<div class="lbwp-card"><h3 class="lbwp-h3 lbwp-h3--sm">' + esc(t.help_title) + '</h3><div class="lbwp-hints" data-r="hints"></div></div>' +
        '</aside>' +
      '</div>' +
      '<div class="lbwp-sticky" data-r="sticky"><span class="lbwp-sticky__sum"><span class="lbwp-muted" data-r="stickyCount"></span><strong data-r="stickyTotal"></strong></span>' +
        '<button type="button" class="lbwp-btn lbwp-btn--primary" data-act="cart">' + esc(cfg.ctaLabel) + '</button></div>';

    this.r = {};
    Array.prototype.forEach.call(app.querySelectorAll('[data-r]'), function (n) { self.r[n.getAttribute('data-r')] = n; });
    this.app = app;
    this.bind();
  };

  p.presetLabel = function (pr) {
    var self = this, cnt = {}, total = 0;
    (pr.items || []).forEach(function (r) { if (self.els[r[0]]) cnt[r[0]] = (cnt[r[0]] || 0) + 1; });
    Object.keys(cnt).forEach(function (k) { total += Math.ceil(cnt[k] / self.els[k].setSize); });
    return pr.name + ' · ' + (total === 1 ? this.t.count_one : fmt(this.t.count_other, { n: total }));
  };

  /* ---------- Ereignisse ---------- */
  p.bind = function () {
    var self = this, app = this.app;
    app.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b || !app.contains(b)) return;
      var act = b.getAttribute('data-act'), val = b.getAttribute('data-val');
      switch (act) {
        case 'frame': self.set({ frame: val, felt: FELT_FOR[val], pick: {} }); break;
        case 'pick':
          var pv = val.split(':'), pk = {};
          for (var k0 in self.state.pick) pk[k0] = self.state.pick[k0];
          pk[pv[0]] = +pv[1];
          self.set({ pick: pk });
          break;
        case 'preset':
          var pr = self.cfg.presets[+val];
          self.set({ items: self.presetItems(pr), sel: null });
          self.emit('preset', { name: pr.name });
          break;
        case 'clear': self.set({ items: [], sel: null }); break;
        case 'add': self.add(val); break;
        case 'move': var d = val.split(','); self.move(+d[0], +d[1]); break;
        case 'rot': self.rotate(+val); break;
        case 'flip': self.flip(); break;
        case 'removeSel': self.removeItem(self.state.sel); break;
        case 'removeLine': self.removeLast(val); break;
        case 'togglePhoto': self.set({ showPhoto: !self.state.showPhoto }); break;
        case 'removePhoto':
          if (self.state.photo) URL.revokeObjectURL(self.state.photo);
          self.set({ photo: null, photoSize: null, saved: false }); break;
        case 'export': self.exportImage(); break;
        case 'share': self.share(); break;
        case 'cart': self.addToCart(); break;
      }
    });
    app.addEventListener('change', function (e) {
      if (e.target.getAttribute('data-act') !== 'photoFile') return;
      var f = e.target.files && e.target.files[0];
      if (!f) return;
      if (self.state.photo) URL.revokeObjectURL(self.state.photo);
      var url = URL.createObjectURL(f), im = new Image();
      im.onload = function () { self.set({ photoSize: [im.naturalWidth, im.naturalHeight] }); };
      im.src = url;
      self.set({ photo: url, photoSize: null, lines: { top: 0.06, floor: 0.82 }, saved: false });
    });
    app.addEventListener('input', function (e) {
      var act = e.target.getAttribute('data-act');
      if (act !== 'wallW' && act !== 'wallH') return;
      var v = parseFloat(String(e.target.value).replace(',', '.'));
      var lim = act === 'wallW' ? [50, 1000] : [50, 600];
      if (!(v >= lim[0] && v <= lim[1])) return;
      self.state[act] = v;
      self.state.items = self.state.items.map(function (it) { return self.place(it); });
      self.render();
    });

    /* Ziehen mit Maus, Stift und Finger */
    var drag = null;
    var lineDrag = null;
    app.addEventListener('pointerdown', function (e) {
      var ln = e.target.closest('[data-line]');
      if (ln) {
        lineDrag = ln.getAttribute('data-line');
        try { ln.setPointerCapture(e.pointerId); } catch (err) {}
        e.preventDefault();
        return;
      }
      var b = e.target.closest('.lbwp-item');
      if (!b || e.button > 0) return;
      var it = self.item(b.getAttribute('data-id'));
      if (!it) return;
      var rect = self.r.wall.getBoundingClientRect();
      drag = { id: it.id, el: b, sx: e.clientX, sy: e.clientY, x: it.x, top: it.top, k: self.state.wallW / rect.width, moved: false };
      try { b.setPointerCapture(e.pointerId); } catch (err) {}
    });
    app.addEventListener('pointermove', function (e) {
      if (lineDrag) {
        var rc = self.r.markBox.getBoundingClientRect();
        e.preventDefault();
        self.setLine(lineDrag, (e.clientY - rc.top) / rc.height);
        return;
      }
      if (!drag) return;
      var dx = e.clientX - drag.sx, dy = e.clientY - drag.sy;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
      drag.moved = true;
      e.preventDefault();
      var it = self.item(drag.id);
      it.x = drag.x + dx * drag.k;
      it.top = drag.top - dy * drag.k;
      self.place(it);
      self.positionNode(drag.el, it);
    });
    var end = function () {
      if (lineDrag) { lineDrag = null; return; }
      if (!drag) return;
      var d = drag; drag = null;
      if (d.moved) self.set({ sel: d.id });
      else self.set({ sel: self.state.sel === d.id ? null : d.id });
    };
    app.addEventListener('pointerup', end);
    app.addEventListener('pointercancel', end);
    /* Klicks auf Elemente werden über pointerup behandelt; Tastatur-„Klick“ hier */
    app.addEventListener('keydown', function (e) {
      var ln = e.target.closest && e.target.closest('[data-line]');
      if (ln && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        var w = ln.getAttribute('data-line');
        self.setLine(w, self.state.lines[w] + (e.key === 'ArrowUp' ? -0.005 : 0.005) * (e.shiftKey ? 5 : 1));
        return;
      }
      var b = e.target.closest && e.target.closest('.lbwp-item');
      if (!b) return;
      var id = b.getAttribute('data-id'), step = e.shiftKey ? 1 : 5, k = e.key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); self.set({ sel: self.state.sel === id ? null : id }); return; }
      var moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
      if (moves[k]) { e.preventDefault(); self.state.sel = id; self.move(moves[k][0], moves[k][1]); return; }
      if (k === 'Delete' || k === 'Backspace') { e.preventDefault(); self.removeItem(id); }
    });

    /* Mobile Leiste nur zeigen, solange der Planer im Bild ist */
    if ('IntersectionObserver' in window && this.r.sticky) {
      new IntersectionObserver(function (en) {
        self.root.classList.toggle('lbwp-in-view', en[0].isIntersecting);
      }, { threshold: 0.05 }).observe(app.querySelector('.lbwp-layout'));
    }
  };

  /* ---------- Aktionen ---------- */
  p.item = function (id) { return this.state.items.filter(function (i) { return i.id === id; })[0]; };
  p.add = function (key) {
    var el = this.els[key];
    if (!el || !el.available) return;
    var n = this.state.items.filter(function (i) { return i.key === key; }).length;
    var stp = el.setSize > 1;
    var it = this.place({ id: 'i' + (this.seq++), key: key, x: el.def[0] + n * (stp ? 28 : 13), top: el.def[1] + n * (stp ? 30 : -12), rot: 0 });
    this.set({ items: this.state.items.concat([it]), sel: it.id });
    this.emit('add', { element: key });
  };
  p.move = function (dx, dtop) {
    var it = this.item(this.state.sel);
    if (!it) return;
    it.x += dx; it.top += dtop;
    this.place(it);
    this.render();
  };
  p.rotate = function (d) {
    var it = this.item(this.state.sel);
    if (!it || !this.els[it.key].rotatable) return;
    it.rot = (((it.rot || 0) + d) % 360 + 360) % 360;
    this.render();
  };
  /* Spiegeln (z. B. CORNER: linke ↔ rechte Ecke) */
  p.flip = function () {
    var it = this.item(this.state.sel);
    if (!it || !this.els[it.key].mirrorable) return;
    it.flip = !it.flip;
    this.render();
  };
  p.removeItem = function (id) {
    var it = this.item(id);
    if (!it) return;
    this.set({ items: this.state.items.filter(function (i) { return i.id !== id; }), sel: this.state.sel === id ? null : this.state.sel });
    this.emit('remove', { element: it.key });
  };
  p.removeLast = function (key) {
    var list = this.state.items.filter(function (i) { return i.key === key; });
    if (list.length) this.removeItem(list[list.length - 1].id);
  };

  /* ---------- Eigenes Foto: zwei Linien im Foto (Oberkante Wand, Wand trifft Boden)
   * bestimmen Maßstab und Lage. Werte als Anteil der Fotohöhe (0 = oben, 1 = unten). ---------- */
  p.photoMap = function () {
    var s = this.state;
    if (!s.photo || !s.photoSize) return null;
    var L = s.lines, span = Math.max(0.05, L.floor - L.top), T = s.wallH + FLOOR_CM;
    var wpct = 100 * s.photoSize[0] * s.wallH / (s.wallW * span * s.photoSize[1]);
    return { width: wpct, left: (100 - wpct) / 2, top: -L.top * s.wallH / (span * T) * 100, span: span };
  };

  /* ---------- Rendering ---------- */
  p.svg = function (g, standalone, px) {
    var sh = g.shape;
    var out = '<svg ' + (standalone ? 'xmlns="http://www.w3.org/2000/svg" width="' + px[0] + '" height="' + px[1] + '" ' : 'aria-hidden="true" focusable="false" ') +
      'viewBox="0 0 ' + sh.w + ' ' + sh.h + '" preserveAspectRatio="none">';
    var sw = standalone ? sh.w / px[0] * 1.2 : 0;
    sh.paths.forEach(function (q) {
      out += '<path d="' + q.d + '" fill="' + q.f + '" stroke="' + q.s + '" stroke-width="' + (standalone ? n2(q.w * sw) : q.w) + '"' + (standalone ? '' : ' vector-effect="non-scaling-stroke"') + '/>';
    });
    return out + '</svg>';
  };
  p.positionLines = function () {
    var L = this.state.lines, box = this.r.markBox;
    if (!box) return;
    box.querySelector('[data-line="top"]').style.top = (L.top * 100) + '%';
    box.querySelector('[data-line="floor"]').style.top = (L.floor * 100) + '%';
    Array.prototype.forEach.call(box.querySelectorAll('[data-line]'), function (n) {
      n.setAttribute('aria-valuenow', Math.round(L[n.getAttribute('data-line')] * 100));
    });
  };
  p.setLine = function (which, v) {
    var L = { top: this.state.lines.top, floor: this.state.lines.floor };
    L[which] = which === 'top' ? clamp(v, 0, L.floor - 0.08) : clamp(v, L.top + 0.08, 1);
    this.state.lines = L;
    this.render();
  };
  p.positionNode = function (node, it) {
    var s = this.state, g = this.geom(it.key);
    node.style.left = (it.x / s.wallW * 100) + '%';
    var T = s.wallH + FLOOR_CM;
    node.style.top = ((s.wallH - it.top) / T * 100) + '%';
    node.style.width = (g.w / s.wallW * 100) + '%';
    node.style.height = (g.h / T * 100) + '%';
    node.style.transform = it.rot ? 'rotate(' + it.rot + 'deg)' : '';
    var sv = node.querySelector('svg');
    if (sv) sv.style.transform = it.flip ? 'scaleX(-1)' : '';
    var tag = node.querySelector('.lbwp-tag');
    if (tag) tag.style.transform = 'translateX(-50%)' + (it.rot ? ' rotate(' + (-it.rot) + 'deg)' : '');
  };

  p.render = function () {
    var self = this, s = this.state, t = this.t, r = this.r, cfg = this.cfg;
    var focusKey = document.activeElement && this.app.contains(document.activeElement) ? document.activeElement.getAttribute('data-focus') : null;
    var tot = this.totals();

    /* Farben */
    Array.prototype.forEach.call(this.app.querySelectorAll('[data-act="frame"]'), function (b) {
      var on = s[b.getAttribute('data-act')] === b.getAttribute('data-val');
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    r.wallLabel.textContent = fmt(t.wall_size, { w: Math.round(s.wallW), h: Math.round(s.wallH) }) + (s.photo ? ' · ' + t.your_photo : '');

    /* Foto */
    if (r.photo) {
      r.photo.hidden = !s.showPhoto;
      this.app.querySelector('[data-act="togglePhoto"]').setAttribute('aria-expanded', s.showPhoto ? 'true' : 'false');
      r.photoBtn.textContent = s.photo ? t.photo_change : t.photo_pick;
      r.photoActions.hidden = !s.photo;
      r.photoHint.textContent = s.saved ? t.photo_hint_saved : (s.photo ? t.photo_hint_set : t.photo_hint_empty);
      var wi = this.app.querySelector('[data-act="wallW"]'), hi = this.app.querySelector('[data-act="wallH"]');
      if (document.activeElement !== wi) wi.value = Math.round(s.wallW);
      if (document.activeElement !== hi) hi.value = Math.round(s.wallH);
      r.mark.hidden = !s.photo;
      if (s.photo) {
        if (r.markImg.getAttribute('src') !== s.photo) r.markImg.setAttribute('src', s.photo);
        this.positionLines();
        r.lineTopLabel.textContent = fmt(t.line_top, { h: Math.round(s.wallH) });
      }
    }

    /* Wand */
    var wall = r.wall;
    var T = s.wallH + FLOOR_CM;
    wall.style.aspectRatio = s.wallW + ' / ' + T;
    wall.style.backgroundImage = '';
    wall.classList.toggle('has-photo', !!s.photo);
    var html = '';
    var pm = this.photoMap();
    if (pm) html += '<img class="lbwp-wall__photo" alt="" src="' + esc(s.photo) + '" style="left:' + n2(pm.left) + '%;top:' + n2(pm.top) + '%;width:' + n2(pm.width) + '%">';
    for (var cm = 50; cm < s.wallH; cm += 50) {
      html += '<div class="lbwp-ruler" style="top:' + ((s.wallH - cm) / T * 100) + '%"><span>' + cm + ' cm</span></div>';
    }
    html += '<div class="lbwp-floor' + (s.photo ? ' is-photo' : '') + '" style="top:' + (s.wallH / T * 100) + '%">' + (s.photo ? '' : parquet(s.wallW)) + '</div>';
    s.items.forEach(function (it) {
      var g = self.geom(it.key), on = it.id === s.sel, el = g.el;
      var so = g.status === 'soldout';
      html += '<button type="button" class="lbwp-item' + (on ? ' is-selected' : '') + (el.floor ? ' is-floor' : '') + (so ? ' is-soldout' : '') + '" data-id="' + it.id + '" data-focus="item-' + it.id + '" aria-pressed="' + on + '" aria-label="' + esc(el.title + (so ? ', ' + t.soldout_tag : '') + (on ? ', ' + t.selected_short : '')) + '">' +
        self.svg(g) + '<span class="lbwp-tag' + (el.setSize > 1 && !on && !so ? ' is-hidden' : '') + '" aria-hidden="true">' + esc(el.name) + (so ? ' · ' + esc(t.soldout_tag) : '') + '</span></button>';
    });
    if (!s.items.length) html += '<div class="lbwp-empty">' + esc(t.empty_wall) + '</div>';
    wall.innerHTML = html;
    Array.prototype.forEach.call(wall.querySelectorAll('.lbwp-item'), function (node) { self.positionNode(node, self.item(node.getAttribute('data-id'))); });

    /* Auswahl-Leiste */
    var sel = this.item(s.sel);
    if (sel) {
      var sel_el = this.els[sel.key];
      var ab = function (act, val, label, path) {
        return '<button type="button" class="lbwp-icon" data-act="' + act + '" data-val="' + val + '" data-focus="' + act + val + '" aria-label="' + esc(label) + '"><svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + path + '</svg></button>';
      };
      r.selbar.innerHTML = '<div class="lbwp-selbar"><span>' + fmt(esc(t.selected), { name: '<strong>' + esc(sel_el.title) + '</strong>' }) + '</span><div class="lbwp-selbar__btns">' +
        ab('move', '-5,0', t.left, '<path d="M15 18l-6-6 6-6"/>') +
        (sel_el.floor ? '' : ab('move', '0,5', t.up, '<path d="M18 15l-6-6-6 6"/>') + ab('move', '0,-5', t.down, '<path d="M6 9l6 6 6-6"/>')) +
        ab('move', '5,0', t.right, '<path d="M9 18l6-6-6-6"/>') +
        (sel_el.rotatable ? '<span class="lbwp-rot">' + ab('rot', '-15', t.rot_left, '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>') +
          '<span class="lbwp-rot__val">' + (sel.rot || 0) + '°</span>' + ab('rot', '15', t.rot_right, '<path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/>') + '</span>' : '') +
        (sel_el.mirrorable ? ab('flip', '', t.mirror, '<path d="M12 3v18"/><path d="M8 7l-5 5 5 5z"/><path d="M16 7l5 5-5 5z"/>') : '') +
        '<button type="button" class="lbwp-btn lbwp-btn--small" data-act="removeSel" data-focus="removeSel">' + esc(t.remove) + '</button></div></div>';
    } else {
      r.selbar.innerHTML = '<p class="lbwp-muted lbwp-tip">' + esc(t.tip) + '</p>';
    }

    /* Elemente hinzufügen */
    var free = {};
    tot.lines.forEach(function (l) { free[l.key] = l.free; });
    r.groups.innerHTML = (cfg.groups || []).map(function (gr) {
      var items = self.order.filter(function (k) { return self.els[k].group === gr.key && self.els[k].available; });
      if (!items.length) return '';
      return '<div class="lbwp-group"><div class="lbwp-group__head"><span class="lbwp-group__title">' + esc(gr.title) + '</span><span class="lbwp-muted">' + esc(gr.hint) + '</span></div>' +
        items.map(function (k) {
          var el;
          var rv = self.resolve(el = self.els[k]), v = rv.v, f = free[k] || 0;
          var sub = f ? fmt(t.free_left, { n: f }) :
            rv.status === 'soldout' ? t.soldout_sub :
            rv.status === 'combo' ? fmt(t.combo_sub, { chosen: self.chosenLabel(el) }) : el.sub;
          var price = el.setSize > 1 ? (f ? t.included : fmt(t.per_set, { price: self.money(v.price), n: el.setSize })) : self.money(v.price);
          return '<button type="button" class="lbwp-add' + (rv.status !== 'ok' && !f ? ' is-' + rv.status : '') + '" data-act="add" data-val="' + k + '" data-focus="add-' + k + '">' +
            '<span class="lbwp-add__plus" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square"><path d="M12 5v14M5 12h14"/></svg></span>' +
            '<span class="lbwp-add__text"><span class="lbwp-add__name">' + esc(el.name) + '</span><span class="lbwp-muted">' + esc(sub) + '</span></span>' +
            '<span class="lbwp-add__price' + (f ? ' is-included' : '') + '">' + esc(price) + '</span></button>';
        }).join('') + '</div>';
    }).join('');

    /* Zusammenfassung */
    var d = cfg.discount;
    r.count.textContent = tot.count === 1 ? t.count_one : fmt(t.count_other, { n: tot.count });
    var tiers = this.tiers();
    if (tiers.length) {
      var top = tiers[tiers.length - 1].min, cnt = Math.min(tot.eligible, top);
      var title = tot.unlocked ? fmt(t.prog_unlocked, { pct: tot.pct }) : fmt(t.prog_locked, { pct: tiers[0].pct, min: tiers[0].min });
      var text;
      if (!tot.unlocked) {
        var rest = tiers[0].min - tot.eligible;
        text = rest === 1 ? t.prog_rest_one : fmt(t.prog_rest_other, { n: rest });
      } else if (tot.next) {
        var rn = tot.next.min - tot.eligible;
        text = fmt(rn === 1 ? t.prog_next_one : t.prog_next_other, { n: rn, pct: tot.next.pct });
      } else {
        text = d.mode === 'code' ? fmt(t.prog_done, { code: d.code }) : t.prog_done_auto;
      }
      var ticks = tiers.map(function (tr) {
        return '<span class="lbwp-progress__tick' + (tot.eligible >= tr.min ? ' is-on' : '') + '" style="left:' + (tr.min / top * 100) + '%"><i>' + tr.pct + ' %</i></span>';
      }).join('');
      r.progress.innerHTML = '<div class="lbwp-progress"><div class="lbwp-progress__row"><strong>' + esc(title) + '</strong><span>' + cnt + ' / ' + top + '</span></div>' +
        '<div class="lbwp-progress__track"><div class="lbwp-progress__bar"><span style="width:' + (cnt / top * 100) + '%"></span></div>' + (tiers.length > 1 ? ticks : '') + '</div>' +
        '<span class="lbwp-muted">' + esc(text) + '</span></div>';
    } else {
      r.progress.innerHTML = '';
    }
    r.lines.innerHTML = tot.lines.map(function (l) {
      var el = l.el, v = l.v, fr = v && v.frame ? v.frame : s.frame, fe = v && v.felt && v.felt !== 'x' ? v.felt : s.felt;
      var name = el.setSize > 1 ? fmt(t.set_line, { name: el.title, n: el.setSize }) + (l.qty > 1 ? ' × ' + l.qty : '') : el.title + (l.qty > 1 ? ' × ' + l.qty : '');
      var info = (el.setSize > 1 ? fmt(t.set_placed, { placed: l.placed, total: l.qty * el.setSize }) + (l.free ? ' · ' + fmt(t.set_free, { n: l.free }) : '') + ' · ' : '') + (v ? self.variantLabel(v) : '');
      return '<div class="lbwp-line' + (l.status === 'soldout' ? ' is-soldout' : '') + '"><span class="lbwp-swatch" aria-hidden="true" style="background:linear-gradient(' + SWATCH.frame[fr] + ' 0 50%,' + SWATCH.felt[fe] + ' 50% 100%)"></span>' +
        '<span class="lbwp-line__text"><a class="lbwp-line__name" href="' + esc(el.url) + '" target="_blank" rel="noopener">' + esc(name) + '</a><span class="lbwp-muted">' + esc(info) + '</span>' +
        (l.status === 'soldout' ? '<span class="lbwp-note lbwp-note--soldout">' + esc(fmt(t.soldout_line, { variant: self.variantLabel(v) })) + '</span>' +
          (l.alt ? '<button type="button" class="lbwp-alt" data-act="pick" data-val="' + l.key + ':' + l.alt.id + '" data-focus="pick-' + l.key + '">' + esc(fmt(t.soldout_alt, { variant: self.variantLabel(l.alt) })) + '</button>' : '') : '') +
        (l.status === 'combo' ? '<span class="lbwp-note">' + esc(fmt(t.combo_line, { chosen: self.chosenLabel(el), variant: self.variantLabel(v) })) + '</span>' : '') + '</span>' +
        '<span class="lbwp-line__price' + (l.status === 'soldout' ? ' is-soldout' : '') + '">' + self.money(l.price * l.qty) + '</span>' +
        '<button type="button" class="lbwp-x" data-act="removeLine" data-val="' + l.key + '" data-focus="rm-' + l.key + '" aria-label="' + esc(fmt(t.remove_one, { name: el.name })) + '"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>';
    }).join('');
    r.sums.innerHTML = '<div class="lbwp-sums__row"><span>' + esc(t.subtotal) + '</span><span>' + this.money(tot.sub) + '</span></div>' +
      (tot.unlocked ? '<div class="lbwp-sums__row lbwp-sums__row--disc"><span>' + esc(fmt(d.mode === 'code' ? t.discount_line : t.discount_line_auto, { pct: tot.pct, code: d.code })) + '</span><span>− ' + this.money(tot.disc) + '</span></div>' : '') +
      '<div class="lbwp-sums__row lbwp-sums__row--total"><span>' + esc(t.total) + '</span><span>' + this.money(tot.total) + '</span></div>' +
      (cfg.priceNote ? '<span class="lbwp-muted">' + esc(cfg.priceNote) + '</span>' : '') +
      (tot.soldout ? '<span class="lbwp-note lbwp-note--soldout">' + esc(tot.soldout === 1 ? t.soldout_cart_one : fmt(t.soldout_cart_other, { n: tot.soldout })) + '</span>' : '');
    Array.prototype.forEach.call(this.app.querySelectorAll('[data-act="cart"]'), function (b) {
      b.disabled = !tot.count || s.busy;
      b.textContent = s.busy ? t.adding : cfg.ctaLabel;
    });
    r.stickyCount.textContent = r.count.textContent;
    r.stickyTotal.textContent = this.money(tot.total);
    r.msg.textContent = s.msg || '';

    /* Planungshilfe */
    var groupsIn = {}, keysIn = {};
    s.items.forEach(function (it) { groupsIn[self.els[it.key].group] = 1; keysIn[it.key] = 1; });
    var any = function (list, map) { return list.some(function (x) { return map[x]; }); };
    var hints = [];
    var freeTotal = tot.lines.reduce(function (a, l) { return a + l.free; }, 0);
    var combos = tot.lines.filter(function (l) { return l.status === 'combo'; });
    if (combos.length) hints.unshift(['ink', fmt(t.hint_combo, { names: combos.map(function (l) { return l.el.name; }).join(', '), chosen: this.colorLabel(s.frame, s.felt, true) })]);
    if (freeTotal) hints.push(['accent', freeTotal === 1 ? t.hint_free_one : fmt(t.hint_free_other, { n: freeTotal })]);
    if (tot.count && !any(RULES.restGroups, groupsIn) && !any(RULES.restKeys, keysIn)) hints.push(['ink', t.hint_rest]);
    if (tot.count && !any(RULES.scratchGroups, groupsIn)) hints.push(['ink', t.hint_scratch]);
    if (tot.count && !groupsIn.frei && !s.items.some(function (it) { return it.top - self.geom(it.key).h < RULES.entryMaxCm; })) hints.push(['ink', t.hint_entry]);
    hints.push(['accent', t.hint_distance]);
    hints.push(['accent', t.hint_mount]);
    r.hints.innerHTML = hints.map(function (h) { return '<p class="lbwp-hint lbwp-hint--' + h[0] + '">' + esc(h[1]) + '</p>'; }).join('');

    if (focusKey) {
      var f = this.app.querySelector('[data-focus="' + focusKey + '"]');
      if (f && f !== document.activeElement) f.focus({ preventScroll: true });
    }
  };

  /* ---------- Warenkorb ---------- */
  p.addToCart = function () {
    var self = this, cfg = this.cfg, tot = this.totals();
    if (!tot.count || this.state.busy) return;
    var items = [];
    tot.lines.forEach(function (l) { if (l.v && l.status !== 'soldout') items.push({ id: l.v.id, quantity: l.qty }); });
    this.set({ busy: true, msg: '' });
    this.emit('add-to-cart', { items: items, total: tot.total, count: tot.count, unlocked: tot.unlocked });
    fetch(cfg.routes.cartAdd + '.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (j) { if (!res.ok) throw j; return j; });
    }).then(function () {
      var target = cfg.routes.cart;
      if (tot.unlocked && cfg.discount.mode === 'code') target = cfg.routes.discount + encodeURIComponent(cfg.discount.code) + '?redirect=' + encodeURIComponent(cfg.routes.cart);
      window.location.href = target;
    }).catch(function (err) {
      self.set({ busy: false, msg: (err && (err.description || err.message)) || self.t.error_cart });
    });
  };

  /* ---------- Teilen ---------- */
  p.share = function () {
    var self = this, url = window.location.origin + window.location.pathname + '?wand=' + this.shareString();
    var done = function () {
      self.r.shareLabel.textContent = self.t.copied;
      setTimeout(function () { self.r.shareLabel.textContent = self.t.share; }, 2500);
    };
    this.emit('share', { url: url });
    try { window.history.replaceState(null, '', url); } catch (e) {}
    if (navigator.share && window.matchMedia('(pointer:coarse)').matches) {
      navigator.share({ title: document.title, url: url }).catch(function () {});
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () { window.prompt('', url); });
    } else {
      window.prompt('', url);
    }
  };

  /* ---------- Bild speichern (JPG) ---------- */
  p.exportImage = function () {
    var self = this, s = this.state;
    var T = s.wallH + FLOOR_CM;
    var W = 1600, H = Math.round(W * T / s.wallW);
    if (H > 2400) { H = 2400; W = Math.round(H * s.wallW / T); }
    var k = W / s.wallW;
    var cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');
    var load = function (src) {
      return new Promise(function (ok, fail) { var im = new Image(); im.onload = function () { ok(im); }; im.onerror = fail; im.src = src; });
    };
    var FH = Math.round(FLOOR_CM * k), WH = H - FH;
    var bg = s.photo ? load(s.photo) : load('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(parquet(s.wallW, [W, FH])));
    bg.then(function (im) {
      if (s.photo) {
        var L = s.lines, span = Math.max(0.05, L.floor - L.top);
        var sc = s.wallH * k / (span * im.naturalHeight);
        var iw = im.naturalWidth * sc, ih = im.naturalHeight * sc;
        ctx.fillStyle = '#ebe6de'; ctx.fillRect(0, 0, W, H);
        ctx.drawImage(im, (W - iw) / 2, -L.top * ih, iw, ih);
      } else {
        ctx.fillStyle = '#ebe6de'; ctx.fillRect(0, 0, W, WH);
        ctx.drawImage(im, 0, WH, W, FH);
      }
      return Promise.all(s.items.map(function (it) {
        var g = self.geom(it.key), pw = Math.max(1, Math.round(g.w * k)), ph = Math.max(1, Math.round(g.h * k));
        return load('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(self.svg(g, true, [pw, ph]))).then(function (img) {
          return { it: it, img: img, w: pw, h: ph };
        });
      }));
    }).then(function (list) {
      list.forEach(function (o) {
        ctx.save();
        ctx.shadowColor = 'rgba(60,50,40,0.22)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 4;
        ctx.translate(o.it.x * k + o.w / 2, (s.wallH - o.it.top) * k + o.h / 2);
        if (o.it.rot) ctx.rotate(o.it.rot * Math.PI / 180);
        if (o.it.flip) ctx.scale(-1, 1);
        ctx.drawImage(o.img, -o.w / 2, -o.h / 2, o.w, o.h);
        ctx.restore();
      });
      var fs = Math.round(W / 70);
      ctx.font = '600 ' + fs + 'px Poppins, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      var label = self.t.export_footer, tw = ctx.measureText(label).width;
      ctx.fillRect(W - tw - fs * 1.6, H - fs * 2.4, tw + fs * 1.6, fs * 2.4);
      ctx.fillStyle = '#3f4040';
      ctx.fillText(label, W - fs * 0.8, H - fs * 0.8);
      cv.toBlob(function (blob) {
        if (!blob) return;
        var file = new File([blob], 'lucybalu-wand.jpg', { type: 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] }) && window.matchMedia('(pointer:coarse)').matches) {
          navigator.share({ files: [file] }).catch(function () {});
        } else {
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob); a.download = 'lucybalu-wand.jpg';
          document.body.appendChild(a); a.click();
          setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
        }
        self.set({ saved: true });
        self.emit('export', {});
      }, 'image/jpeg', 0.9);
    }).catch(function () { self.set({ msg: self.t.error_export }); });
  };

  /* ======================================================================
   * Start
   * ==================================================================== */
  var instances = [];
  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-lbwp]'), function (root) {
      if (root.__lbwp) return;
      var node = root.querySelector('[data-lbwp-config]');
      if (!node) return;
      try {
        root.__lbwp = new Planner(root, JSON.parse(node.textContent));
        instances.push(root.__lbwp);
      } catch (e) {
        if (window.console) console.error('[lbwp]', e);
      }
    });
  }
  window.LBWallPlanner = { shapes: SHAPES, palette: palette, classify: classify, instances: instances, init: init, Planner: Planner };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
